import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideStepTabs from "@/features/pathpick/features/start-guide/components/PathPickStartGuideStepTabs";
import PathPickStartGuideSellingProfileSection from "@/features/pathpick/features/start-guide/components/onboarding/PathPickStartGuideSellingProfileSection";
import PathPickStartGuideSmartStorePreflight from "@/features/pathpick/features/start-guide/components/onboarding/PathPickStartGuideSmartStorePreflight";
import PathPickStartGuideReadinessList from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideReadinessList";
import PathPickStartGuideResetTestValuesDialog from "@/features/pathpick/features/start-guide/components/PathPickStartGuideResetTestValuesDialog";
import useStartGuideStorage from "@/features/pathpick/features/start-guide/hooks/useStartGuideStorage";
import useStartGuideReadiness from "@/features/pathpick/features/start-guide/hooks/useStartGuideReadiness";
import useStartGuideDemoState from "@/features/pathpick/features/start-guide/hooks/useStartGuideDemoState";
import PathPickStartGuideProductTour from "@/features/pathpick/features/start-guide/components/product-tour/PathPickStartGuideProductTour";
import PathPickStartGuideWorkbench from "@/features/pathpick/features/start-guide/components/workbench/PathPickStartGuideWorkbench";
import PathPickStartGuideRequestHistoryTour from "@/features/pathpick/features/start-guide/components/after-order/PathPickStartGuideRequestHistoryTour";
import PathPickStartGuideOrderTour from "@/features/pathpick/features/start-guide/components/after-order/PathPickStartGuideOrderTour";
import PathPickStartGuideOrderDetailTour from "@/features/pathpick/features/start-guide/components/after-order/PathPickStartGuideOrderDetailTour";
import PathPickStartGuideMembershipSection from "@/features/pathpick/features/start-guide/components/membership/PathPickStartGuideMembershipSection";
import PathPickStartGuideFaqSection from "@/features/pathpick/features/start-guide/components/faq/PathPickStartGuideFaqSection";
import PathPickStartGuideFinishSection from "@/features/pathpick/features/start-guide/components/finish/PathPickStartGuideFinishSection";
import {
  START_GUIDE_STEPS,
  StartGuideAfterOrderPage,
  StartGuideStep,
} from "@/features/pathpick/features/start-guide/types";
import { Helmet } from "react-helmet-async";

// `설정` 단계에서 어느 화면을 보여줄지 — 판매 방식 선택 → 사전 확인 → 설정 목록 순이다.
// 앞의 둘은 탭이 열리기 전 온보딩 관문이라 components/onboarding 에, 마지막만 components/setup 에 있다.
type SetupScreen = "profile" | "preflight" | "readiness";

// 개발/목 환경에서만 노출되는 초기화 버튼 (전달서 §8).
const IS_MOCK_ENV = process.env.NODE_ENV !== "production";

/**
 * 패스픽 시작 가이드.
 *
 * 화면은 크게 둘로 나뉜다. 판매 방식을 정하고 스마트스토어 개설을 확인하는 동안은
 * 단계 탭 없이 온보딩 화면만 보여주고, 설정 목록에 도달한 뒤부터 6개 단계 탭이 열린다.
 * 여기서 만지는 상태는 전부 가이드 전용이라 실제 담은 상품·등록 신청·주문에는 닿지 않는다.
 */
function PathPickStartGuidePage() {
  const { t, tTitle } = useI18nHelper();
  // 라벨이 이미 "패스픽 시작 가이드"라 접두사를 붙이지 않는다.
  const pageTitle = tTitle("common:pathpick_start_guide");
  const { state, isHydrated, patch, completeTask, resetTestValues } =
    useStartGuideStorage();
  const readiness = useStartGuideReadiness(state);
  const {
    demo,
    pick,
    toggleSelect,
    runRegistration,
    manageProduct,
    completeOrder,
    requestKreamFile,
    viewRegistrationExample,
    prepareForStep,
    resetDemo,
  } = useStartGuideDemoState();

  // "판매 방식 변경"으로 되돌아온 경우 — 저장값과 무관하게 선택 화면을 강제로 연다.
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // 화면 분기는 전부 저장된 판매 방식 하나에서 나온다.
  // KREAM 을 고르면 스마트스토어 등록 자체가 없다.
  const salesMode = state.salesMode;
  const isKreamOnly = salesMode === "kream";

  // 스마트스토어 등록이 없으면 담은 상품 작업 패널 자체가 할 일이 없다 — 탭에서 통째로 뺀다.
  const visibleSteps = isKreamOnly
    ? START_GUIDE_STEPS.filter((step) => step !== "workbench")
    : START_GUIDE_STEPS;

  // 판매 방식을 바꿔 지금 흐름에 없는 단계가 저장돼 있으면 그 자리를 대신하는 단계로 보정한다.
  const currentStep = visibleSteps.includes(state.currentStep)
    ? state.currentStep
    : "after_order";

  // `신청 후 이용`(KREAM 전용: `주문 후 이용`) 탭 안의 진행.
  //   스마트스토어(API·엑셀) : 등록 신청 내역 → 주문 → 주문 상세
  //   KREAM                  : 주문 → 주문 상세 → 등록 신청 내역
  // KREAM 은 주문 상세에서 신청한 입찰용 파일을 등록 신청 내역에서 받으므로 순서가 뒤집힌다.
  // 멤버십은 이 안의 마지막 화면이었으나 탭으로 승격했다 — 여기서는 다루지 않는다.
  // 진입 화면·마지막 화면·저장값 유효성·앞뒤 이동이 모두 이 배열 하나에서 나온다.
  const afterOrderSequence: StartGuideAfterOrderPage[] = isKreamOnly
    ? ["order", "orderDetail", "requestHistory"]
    : ["requestHistory", "order", "orderDetail"];

  // 보던 화면은 저장한다(새로고침해도 유지). 판매 방식을 바꿔 지금 흐름에 없는 화면이
  // 저장돼 있을 수 있으니 그때는 그 흐름의 첫 화면으로 되돌린다.
  const afterOrderPage = afterOrderSequence.includes(state.afterOrderPage)
    ? state.afterOrderPage
    : afterOrderSequence[0];
  const afterOrderIndex = afterOrderSequence.indexOf(afterOrderPage);

  const [isResetOpen, setIsResetOpen] = useState(false);

  // 목 상태를 채울 때 쓰는 등록 방식 — KREAM 은 등록 단계 자체가 없다.
  const demoMethod = salesMode === "kream" ? ("none" as const) : salesMode;

  const setStep = (step: StartGuideStep) => {
    patch({
      currentStep: step,
      // 탭으로 후단계를 열면 그 판매 방식의 첫 화면부터 시작한다.
      ...(step === "after_order"
        ? { afterOrderPage: afterOrderSequence[0] }
        : {}),
    });
    // 뒤 단계를 바로 열면 그 화면에 필요한 목 상품 상태만 자동으로 채운다(전달서 §4).
    prepareForStep(step, demoMethod);
  };

  // 이 페이지가 곧 스크롤 컨테이너다 — 앱 셸이 100vh 고정이라 window 스크롤은 없다.
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 새로고침으로 뒤 단계에 바로 들어온 경우 — 탭으로 건너뛴 것과 같은 준비를 한 번 해 준다.
  // 목 상태는 저장하지 않아서(전달서 §18) 이게 없으면 담은 상품이 빈 채로 그려진다.
  const hasPreparedRef = useRef(false);
  useEffect(() => {
    if (!isHydrated || hasPreparedRef.current) {
      return;
    }
    hasPreparedRef.current = true;
    prepareForStep(currentStep, demoMethod);
  }, [isHydrated, currentStep, demoMethod, prepareForStep]);

  const setupScreen: SetupScreen = (() => {
    if (isEditingProfile || !state.hasSavedProfile) {
      return "profile";
    }
    // KREAM 판매 방식이면 개설 확인 자체가 필요 없다(전달서 §5·§24).
    if (isKreamOnly) {
      return "readiness";
    }
    return state.isPreflightConfirmed ? "readiness" : "preflight";
  })();

  // 판매 방식을 정하고 개설을 확인하기 전까지는 단계 탭을 열지 않는다.
  // 앞 선택이 뒤 단계의 내용을 통째로 바꾸기 때문에 순서를 건너뛸 수 없는 구간이다.
  const isOnboarding = currentStep === "setup" && setupScreen !== "readiness";

  // 웰컴 문구는 판매 방식 선택 화면에만 남는다. 그 뒤로는 각 화면이 자기 제목을 갖는다.
  const pageHeading = (() => {
    if (!isOnboarding) {
      return null;
    }
    return setupScreen === "profile"
      ? {
          title: t("path_pick_guide:page.title"),
          description: t("path_pick_guide:page.description"),
        }
      : {
          title: t("path_pick_guide:preflight.title"),
          description: t("path_pick_guide:preflight.description"),
        };
  })();

  const renderSetupStep = () => {
    if (setupScreen === "profile") {
      return (
        <PathPickStartGuideSellingProfileSection
          // 아직 저장한 적이 없으면 아무것도 고르지 않은 채로 시작한다.
          initialSalesMode={state.hasSavedProfile ? state.salesMode : null}
          onSubmit={(nextSalesMode) => {
            // 판매 방식을 저장할 때마다 사전 확인을 처음부터 다시 받는다 —
            // 어느 방식을 고르든 그 방식 기준으로 개설 상태를 다시 확인해야 한다.
            // KREAM 은 아래 setupScreen 이 사전 확인을 건너뛰므로 여기서 가리지 않는다.
            patch({
              salesMode: nextSalesMode,
              hasSavedProfile: true,
              isPreflightConfirmed: false,
              smartStorePresence: null,
            });
            setIsEditingProfile(false);
          }}
        />
      );
    }

    if (setupScreen === "preflight") {
      return (
        <PathPickStartGuideSmartStorePreflight
          presence={state.smartStorePresence}
          onChangePresence={(smartStorePresence) =>
            patch({ smartStorePresence })
          }
          onConfirm={() => patch({ isPreflightConfirmed: true })}
          onReselectProfile={() => setIsEditingProfile(true)}
        />
      );
    }

    // KREAM 판매 방식은 설정할 것이 없다 — 요약 카드가 그 이유를 진행률 자리에서 말한다
    // (`readiness.kream.all_done_progress`). 아래에 같은 말을 반복하는 박스를 덧붙이지 않는다.
    return (
      <PathPickStartGuideReadinessList
        readiness={readiness}
        salesMode={salesMode}
        onCompleteTask={completeTask}
        onGoToProductTour={() => setStep("product_tour")}
      />
    );
  };

  // 신청 버튼은 아래쪽 미리보기 안에 있지만 완료 안내와 `다음` 버튼은 화면 위에 생긴다.
  // 뷰포트가 낮으면 신청해도 결과가 안 보이므로 신청 직후 가이드 본문을 맨 위로 올린다.
  // 모달이 닫히면 Chakra 가 포커스를 아래쪽 신청 버튼으로 되돌리는데(preventScroll 없이)
  // 그때 브라우저가 그 버튼을 다시 보이게 스크롤해 우리 이동을 덮어쓴다.
  // 포커스 복원은 언마운트 직후 마이크로태스크라서, 다음 프레임까지 미뤄야 살아남는다.
  const handleRequestKreamFile = () => {
    requestKreamFile();
    requestAnimationFrame(() => {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  // 후단계 안의 앞뒤 이동 — 시퀀스의 양 끝에서는 탭 밖으로 나간다.
  const goPrevInAfterOrder = () => {
    const prev = afterOrderSequence[afterOrderIndex - 1];
    if (prev) {
      patch({ afterOrderPage: prev });
      return;
    }
    setStep(isKreamOnly ? "product_tour" : "workbench");
  };

  const goNextInAfterOrder = () => {
    const next = afterOrderSequence[afterOrderIndex + 1];
    if (next) {
      patch({ afterOrderPage: next });
      return;
    }
    setStep("membership");
  };

  const renderAfterOrderStep = () => {
    // 신청 후 이용 안에서만 쓰는 내부 진행. 스마트스토어 사용자는 등록 신청 내역을 먼저 보고,
    // KREAM 단독 사용자는 주문 → 주문 상세 → 등록 신청 내역 순으로 본다(전달서 §5·§12·§14).
    if (afterOrderPage === "requestHistory") {
      return (
        <PathPickStartGuideRequestHistoryTour
          demo={demo}
          salesMode={salesMode}
          stepIndex={afterOrderIndex}
          stepTotal={afterOrderSequence.length}
          onManageProduct={manageProduct}
          onPrev={goPrevInAfterOrder}
          onNext={goNextInAfterOrder}
        />
      );
    }
    if (afterOrderPage === "orderDetail") {
      return (
        <PathPickStartGuideOrderDetailTour
          demo={demo}
          salesMode={salesMode}
          stepIndex={afterOrderIndex}
          stepTotal={afterOrderSequence.length}
          onRequestKreamFile={handleRequestKreamFile}
          onPrev={goPrevInAfterOrder}
          // KREAM 은 신청한 파일을 등록 신청 내역에서 받는다 — 멤버십은 그 뒤다.
          onNext={goNextInAfterOrder}
        />
      );
    }
    return (
      <PathPickStartGuideOrderTour
        demo={demo}
        isKreamOnly={isKreamOnly}
        stepIndex={afterOrderIndex}
        stepTotal={afterOrderSequence.length}
        onToggleSelect={toggleSelect}
        onCompleteOrder={completeOrder}
        onPrev={goPrevInAfterOrder}
        onNext={goNextInAfterOrder}
      />
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case "setup":
        return renderSetupStep();
      case "product_tour":
        return (
          <PathPickStartGuideProductTour
            pickedIds={demo.pickedIds}
            onPick={pick}
            onPrev={() => setStep("setup")}
            onNext={() => setStep(isKreamOnly ? "after_order" : "workbench")}
          />
        );
      case "workbench":
        return (
          <PathPickStartGuideWorkbench
            demo={demo}
            salesMode={salesMode}
            onToggleSelect={toggleSelect}
            onRunRegistration={runRegistration}
            onViewExample={viewRegistrationExample}
            onPrev={() => setStep("product_tour")}
            onNext={() => setStep("after_order")}
          />
        );
      case "after_order":
        return renderAfterOrderStep();
      case "membership":
        return (
          <PathPickStartGuideMembershipSection
            onPrev={() => {
              // 후단계 시퀀스의 마지막 화면으로 돌아간다 — setStep 은 처음으로 되감는다.
              patch({
                currentStep: "after_order",
                afterOrderPage:
                  afterOrderSequence[afterOrderSequence.length - 1],
              });
            }}
            onNext={() => setStep("faq")}
          />
        );
      case "faq":
        return (
          <PathPickStartGuideFaqSection
            onPrev={() => setStep("membership")}
            onNext={() => setStep("finish")}
          />
        );
      case "finish":
        return (
          <PathPickStartGuideFinishSection
            onStartFreeTrial={(freeTrialStartedAt) =>
              patch({ freeTrialStartedAt })
            }
            onPrev={() => setStep("faq")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        padding: "16px",
        overflow: "auto",
        height: "100%",
        paddingBottom: "100px",
      }}
    >
      <Helmet>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} />
      </Helmet>
      {/* 본문은 960px로 묶어 가운데 정렬한다(전달서 §4). */}
      <Flex
        flexDirection={"column"}
        gap={"16px"}
        maxWidth={"960px"}
        margin={"0 auto"}
        paddingX={{ base: "12px", md: "20px" }}
      >
        {/* 저장값을 읽기 전에 그리면 판매 방식 선택 화면이 한 번 깜빡인다. */}
        {isHydrated && (
          <>
            {pageHeading && (
              <Flex
                flexDirection={"column"}
                gap={"6px"}
                padding={"16px"}
                paddingTop={"12px"}
              >
                <Text
                  textStyle={"body-lg-bold"}
                  fontSize={"28px"}
                  color={"#1D1D1B"}
                >
                  {pageHeading.title}
                </Text>
                <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
                  {pageHeading.description}
                </Text>
              </Flex>
            )}

            {!isOnboarding && (
              // 판매 방식 변경/초기화는 한 줄을 따로 차지하지 않고 탭과 같은 줄 우측 끝에 붙는다.
              <PathPickStartGuideStepTabs
                steps={visibleSteps}
                isKreamOnly={isKreamOnly}
                currentStep={currentStep}
                progressValue={
                  (visibleSteps.indexOf(currentStep) + 1) / visibleSteps.length
                }
                onChange={(step) => {
                  setStep(step);
                  // 탭으로 설정을 다시 열면 편집 모드는 풀고 저장된 위치에서 시작한다.
                  setIsEditingProfile(false);
                }}
                rightSlot={
                  <>
                    {IS_MOCK_ENV && (
                      <Text
                        as={"button"}
                        type={"button"}
                        textStyle={"body-sm-medium"}
                        color={"#8F8F8C"}
                        _hover={{ color: "#22221F" }}
                        onClick={() => setIsResetOpen(true)}
                      >
                        {t("path_pick_guide:readiness.reset")}
                      </Text>
                    )}
                    <Text
                      as={"button"}
                      type={"button"}
                      textStyle={"body-sm-bold"}
                      color={"#22221F"}
                      _hover={{ color: "#1272EF" }}
                      onClick={() => {
                        // 링크는 모든 탭 우측에 떠 있지만 선택 화면은 설정 탭 안에서만
                        // 그려진다 — 편집 표시만 켜면 다른 탭에서는 아무 일도 일어나지 않는다.
                        setStep("setup");
                        setIsEditingProfile(true);
                      }}
                    >
                      {t("path_pick_guide:selling_profile.change")}
                    </Text>
                  </>
                }
              />
            )}

            {renderStep()}
          </>
        )}
      </Flex>

      <PathPickStartGuideResetTestValuesDialog
        isOpen={isResetOpen}
        onClose={() => setIsResetOpen(false)}
        onConfirm={() => {
          resetTestValues();
          // 체험 진행은 저장소 밖(메모리)에 있어 따로 비워야 한다.
          resetDemo();
          setIsEditingProfile(false);
          setIsResetOpen(false);
        }}
      />
    </Box>
  );
}

export default PathPickStartGuidePage;
